import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useCallback, useEffect, useState } from 'react';

import { useSession, useSessionUpdateMutation } from '@/hooks/useSwr';

import { ThreadIdleTimeValue } from '../ThreadRuleForm';

dayjs.extend(duration);

function convertSecondsToValueAndUnit(
  seconds: number | null | undefined
): ThreadIdleTimeValue {
  if (seconds == null) {
    return { value: null, unit: 'hours' };
  }

  const dur = dayjs.duration(seconds, 'seconds');

  // Convert to the most appropriate unit (check if it divides evenly)
  const days = dur.asDays();
  const hours = dur.asHours();
  const minutes = dur.asMinutes();

  if (Number.isInteger(days) && days >= 1) {
    return { value: days, unit: 'days' };
  } else if (Number.isInteger(hours) && hours >= 1) {
    return { value: hours, unit: 'hours' };
  } else if (Number.isInteger(minutes)) {
    return { value: minutes, unit: 'minutes' };
  }

  // Default to hours if it doesn't divide evenly
  return { value: hours, unit: 'hours' };
}

function convertValueAndUnitToSeconds(
  idle: ThreadIdleTimeValue
): number | null {
  if (idle.value == null) {
    return null;
  }

  const dur = dayjs.duration(idle.value, idle.unit);
  return dur.asSeconds();
}

export function useThreadIdleSeconds(sessionId: string) {
  const session = useSession(sessionId);
  const threadIdleSeconds = session?.data?.extra?.thread_idle_seconds;

  const [idleTime, setIdleTime] = useState<ThreadIdleTimeValue>(() =>
    convertSecondsToValueAndUnit(threadIdleSeconds)
  );

  // Update local state when session data changes
  useEffect(() => {
    const newIdleTime = convertSecondsToValueAndUnit(threadIdleSeconds);
    setIdleTime(newIdleTime);
  }, [threadIdleSeconds]);

  const sessionUpdateMutation = useSessionUpdateMutation(sessionId);

  const updateSessionIdleTime = useCallback(async () => {
    const seconds = convertValueAndUnitToSeconds(idleTime);

    // Skip mutation if the value hasn't changed
    if (seconds === threadIdleSeconds) {
      return;
    }

    await sessionUpdateMutation.trigger({
      json: {
        extra: {
          ...session?.data?.extra,
          thread_idle_seconds: seconds,
        },
      },
    });
  }, [
    idleTime,
    threadIdleSeconds,
    session?.data?.extra,
    sessionUpdateMutation,
  ]);

  return {
    idleTime,
    setIdleTime,
    updateSessionIdleTime,
  };
}
